/**
 * Maps the engine's wire thread (`ThreadOut`,
 * services/ai-engine/src/api/routers/chat.py) to the package's shape.
 * `id` is a plain integer on the wire (MIN-264 aligned the engine's
 * contract), but it is stringified here — see `ThreadId` — so the engine's
 * integer never leaks past this adapter.
 */

/**
 * Opaque. The engine's integer is stringified at this adapter and never
 * leaks past it — see MIN-297. Gen 1's entry point is an `@ai` annotation on
 * a FHIR Task, so a thread will eventually be anchored to something that is
 * not an ai-engine row; widening this later would be expensive, narrowing it
 * now costs nothing.
 */
export type ThreadId = string;

/** The engine's `ThreadResponse` shape, as received over the wire. */
export interface EngineThread {
  id: number;
  title: string | null;
  model: string | null;
  /** Which `agents.roles` agent answers. */
  role: string;
  subject_patient_id: string | null;
  created_at: string | null;
}

/**
 * A thread, in the package's shape. Field names otherwise mirror the wire
 * shape (both apps' call sites and their existing pure helpers, e.g.
 * `sortChatThreadsByCreatedAt`, already key off it); `subjectPatientId` is
 * the one field every caller now gets that only `apps/provider` modelled
 * before, so it gets a name of its own rather than the wire's `subject_patient_id`.
 */
export interface ChatThread {
  id: ThreadId;
  title: string | null;
  model: string | null;
  /** Which `agents.roles` agent answers. */
  role: string;
  /** The patient this thread is pinned to, or `null` for an unpinned thread. */
  subjectPatientId: string | null;
  created_at: string | null;
}

export function toChatThread(engine: EngineThread): ChatThread {
  return {
    id: String(engine.id),
    title: engine.title,
    model: engine.model,
    role: engine.role,
    subjectPatientId: engine.subject_patient_id,
    created_at: engine.created_at,
  };
}
